// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: label and icon for the Assistant's smaller app actions ("op" cards: move a mail, react to
// a message, mute a chat, change a setting). The card shows the server's title and what Confirm does.

import {ClickUpLogo} from '@app/features/ui/components/icons/ClickUpLogo';
import {WhatsAppLogo} from '@app/features/ui/components/icons/WhatsAppLogo';
import {
	BellIcon,
	CalendarBlankIcon,
	ChatCircleIcon,
	EnvelopeSimpleIcon,
	GearIcon,
	NotePencilIcon,
} from '@app/features/ui/icons/Icons';
import type {AssistantActionDraft} from '@fluxer/schema/src/domains/konvo/AssistantActionDraft';
import type {I18n, MessageDescriptor} from '@lingui/core';
import {msg} from '@lingui/core/macro';
import {IconHome, IconTerminal2} from '@tabler/icons-react';
import type React from 'react';

type OpArea = Extract<AssistantActionDraft, {kind: 'op'}>['area'];

const LABELS: Record<OpArea, MessageDescriptor> = {
	mail: msg({message: 'Email', comment: 'Badge on an Assistant review card for an email action.'}),
	calendar: msg({message: 'Calendar', comment: 'Badge on an Assistant review card for a calendar action.'}),
	task: msg({message: 'Task', comment: 'Badge on an Assistant review card for a ClickUp task action.'}),
	message: msg({message: 'Message', comment: 'Badge on an Assistant review card for a Konvo chat action.'}),
	whatsapp: msg({message: 'WhatsApp', comment: 'Badge on an Assistant review card for a WhatsApp action.'}),
	agents: msg({message: 'Agents', comment: 'Badge on an Assistant review card for a coding agents action.'}),
	settings: msg({message: 'Settings', comment: 'Badge on an Assistant review card that changes a setting.'}),
	home: msg({message: 'Home', comment: 'Badge on an Assistant review card for a Home page action.'}),
	reminder: msg({message: 'Reminder', comment: 'Badge on an Assistant review card for a reminder action.'}),
	pad: msg({message: 'Notes', comment: 'Badge on an Assistant review card for a scratchpad (notes pad) action.'}),
};

const ICONS: Record<OpArea, React.ComponentType<{size?: number}>> = {
	mail: EnvelopeSimpleIcon,
	calendar: CalendarBlankIcon,
	task: ClickUpLogo,
	message: ChatCircleIcon,
	whatsapp: WhatsAppLogo,
	agents: IconTerminal2,
	settings: GearIcon,
	home: IconHome,
	reminder: BellIcon,
	pad: NotePencilIcon,
};

export function opKindLabel(i18n: I18n, area: OpArea): string {
	return i18n._(LABELS[area]);
}

export function opKindIcon(area: OpArea): React.ComponentType<{size?: number}> {
	return ICONS[area];
}
